// Super-Admin only — manage the DeviceType catalog (homeautomation.DeviceType),
// the "product type" master the C# desktop tool edits. List every device type,
// edit any, or add a new one.
//
// Endpoints (backend already exposes them):
//   GetAllDeviceTypeDetails (list — joins ImageData + OTABinFile) ·
//   AddDeviceType (body IS the row; upserts on DeviceName) ·
//   UpdateDeviceType ({ data, where: "DeviceID = N" }).
//
// The list row carries columns from three joined tables. We only ever write the
// DeviceType columns back — DEVICE_TYPE_COLUMNS is that whitelist, so the UPDATE
// never touches an ImageData/OTABinFile column and fails.
//
// ── The Firmware section is the exception, and it writes a DIFFERENT TABLE ──
// `BinFileName`, `HexFileUrl`, `HexFileLocalLocation` and `UartPins` are read
// here through the join, but they live on **OTABinFile** — so they are saved
// with `AddOTABinFile` (which upserts on BinFileName), never with
// UpdateDeviceType. Only `OTABinFileId` and `OtaUpdateDateTime` belong to
// DeviceType, and those are folded into the ordinary payload so the whole edit
// is still one UPDATE. The calls themselves live in core/devices/otaBinFiles.js
// and are shared with the Firmware screen's Publish dialog.
//
// ⚠️ AN OTABinFile ROW IS SHARED BY MANY DEVICE TYPES — id 1 is on more than
// thirty of them. Editing the path or the URL here edits it for EVERY type on
// that file, and uploading under an existing name re-flashes all of them. The
// section names them before it saves: a per-type screen that silently writes
// fleet-wide state is exactly the trap the whitelist above exists to avoid.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Button, Card, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  Tooltip, Alert, MenuItem, Chip, FormControlLabel, Checkbox, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  LinearProgress, Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import MemoryIcon from '@mui/icons-material/Memory';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getDeviceTypes, addDeviceType, updateDeviceType, getOtaBinFiles,
} from '../../../core/api/api';
import { isNativePlatform } from '../../../core/platform';
import {
  uploadBinFile, writeBinFileRow, getBinFileRow, checkBinName, serverStamp,
  getLocalFolder, setLocalFolder, joinLocalPath, EXPECTED_BIN_DIR,
  supportsFolderMemory, pickBinFile,
  readEspImage, chipNameMismatch, notAnAppImage, ESP8266_CLASS, rememberBuildSha,
} from '../../../core/devices/otaBinFiles';
import {
  inspectRemembered, regrantAndRead,
  HANDLE_GRANTED, HANDLE_PROMPT, HANDLE_MISSING,
} from '../../../core/devices/buildHandles';

// ---- The DeviceType table columns we allow writing back (add/update). ----
// Anything NOT here (ImageId, OTABinFileId FKs, joined ImageData/OTABinFile
// columns, server timestamps) is left untouched.
const NUMERIC_COLS = new Set([
  'DeviceID', 'Outputs', 'On_off', 'Dimming', 'Inputs', 'SortOrder',
  'Mrp', 'Warranty', 'Discount', 'PriceWithWifi', 'PriceWithoutWifi',
  'BlankPcb', 'Assembled', 'Tested', 'Packed',
]);
const FLAG_COLS = [
  ['IsMasterDevice', 'Master device'],
  ['CurrentSensor', 'Current sensor'],
  ['SupportsWiFi', 'Supports Wi-Fi'],
  ['SupportsVoice', 'Supports voice'],
  ['SupportsScene', 'Supports scenes'],
  ['ViewOnWebsite', 'Show on website'],
  ['AllowTestingTuya', 'Allow Tuya testing'],
  ['BellPush', 'Bell push'],
  ['Curtain', 'Curtain'],
  ['TouchButtons', 'Touch buttons'],
  ['RGBChannels', 'RGB channels'],
];
const DEVICE_TYPE_OPTS = ['Switch', 'Fan', 'Curtain', 'ColorController', 'SmartLock', 'Sensor', 'Other'];
const ALEXA_TYPE_OPTS = ['Light', 'Fan', 'Curtain', 'Smartlock', 'Others'];

// Every DeviceType column this screen manages — used to build the write payload.
const DEVICE_TYPE_COLUMNS = [
  'DeviceID', 'DeviceInfoId', 'DeviceName', 'DeviceType', 'AlexaType', 'DefaultValue',
  'Outputs', 'On_off', 'Dimming', 'Inputs', 'SortOrder', 'Status', 'Discription',
  'Mrp', 'Warranty', 'Discount', 'PriceWithWifi', 'PriceWithoutWifi',
  'BlankPcb', 'Assembled', 'Tested', 'Packed',
  'OTAUpdateLink', 'OTAUpdateLinkNuvo', 'LocalCodeLink', 'LocalCodeLinkNuvo',
  'ManualPdfLink', 'AppUrlLink', 'ImageLocation', 'ImageType',
  ...FLAG_COLS.map(([k]) => k),
];

const num = (v) => (v === '' || v == null ? '' : Number(v));

// Field widths, in px, by what the value actually is. Named rather than inline
// so a column that is too tight is fixed in one place for every field like it.
const W = {
  count: 92,     // 0-9   channel counts, production stages
  num: 104,      // money and other short numbers
  code: 124,     // short codes: Status, DeviceInfoId, UART pins
  pick: 148,     // the two select boxes
  bin: 330,      // the OTA bin file picker — names run to 32 chars
  med: 170,      // DefaultValue, dates
  name: 230,     // DeviceName, BinFileName
  link: 300,     // URLs and paths — these grow to fill the row
};

// One section: a compact caption over a wrapping row of content-width fields.
// No Divider — a 17px rule between eight sections is 136px of nothing.
function Section({ title, right, children, sx }) {
  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
        >
          {title}
        </Typography>
        {right}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{children}</Box>
    </Box>
  );
}

export default function DeviceTypesScreen() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // The OTABinFile half of the form. Kept apart from `form` on purpose: `form`
  // is the DeviceType row and every key in it goes into the UPDATE, whereas
  // these go to a different endpoint and a different table.
  const [fw, setFw] = useState(null);
  const [fwStep, setFwStep] = useState('');
  const [fwPct, setFwPct] = useState(null);
  // Every OTABinFile row, for the picker. Two columns only — the details behind
  // a selection are fetched on demand, because GetAllOTABinFile does not carry
  // them.
  const [binList, setBinList] = useState([]);
  const [fwBusy, setFwBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    getOtaBinFiles()
      // Row 17 exists with an empty BinFileName and would render as a blank
      // option that silently un-points the device type if picked.
      .then((res) => setBinList((Array.isArray(res.data) ? res.data : [])
        .filter((r) => String(r.BinFileName || '').trim())))
      .catch(() => setBinList([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeviceTypes();
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.DeviceName, r.DeviceType, r.AlexaType, String(r.DeviceID), r.BinFileName]
        .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [list, query]);

  const blankForm = () => {
    const f = {};
    DEVICE_TYPE_COLUMNS.forEach((k) => {
      if (NUMERIC_COLS.has(k)) f[k] = 0;
      else if (FLAG_COLS.some(([fk]) => fk === k)) f[k] = 0;
      else f[k] = '';
    });
    f.DeviceID = '';           // let the admin assign it explicitly
    f.DeviceType = 'Switch';
    f.AlexaType = 'Light';
    f.ImageType = 'Product';
    return f;
  };

  // The joined OTABinFile columns, lifted off the list row. `dirty` is what
  // decides whether the firmware endpoints are called at all — an operator who
  // only renamed the product must not rewrite a bin row thirty types share.
  const blankFw = (r) => ({
    // `originalId` is what the device type pointed at when the dialog opened.
    // Changing the dropdown is a re-point and MUST write DeviceType.OTABinFileId
    // even when no file is uploaded and no field is touched — that selection IS
    // the edit, and treating it as "nothing changed" was the obvious bug here.
    originalId: r && r.OTABinFileId != null ? Number(r.OTABinFileId) : null,
    otaBinFileId: r && r.OTABinFileId != null ? Number(r.OTABinFileId) : null,
    binFileName: (r && r.BinFileName) || '',
    hexFileUrl: (r && r.HexFileUrl) || '',
    localPath: (r && r.HexFileLocalLocation) || '',
    uartPins: (r && r.UartPins) || '',
    hexFileDateTime: (r && r.HexFileDateTime) || '',
    folder: getLocalFolder(),
    file: null,
    image: null,       // the ESP header read off the picked file
    isNew: false,      // "New bin file…" chosen — the name is typed, not picked
    dirty: false,
  });

  const openNew = () => {
    setMsg(''); setEditing(false); setForm(blankForm()); setFw(blankFw(null));
    if (fileRef.current) fileRef.current.value = '';
    setOpen(true);
  };
  const openEdit = (r) => {
    setMsg('');
    setEditing(true);
    const f = {};
    DEVICE_TYPE_COLUMNS.forEach((k) => { f[k] = r[k] ?? (NUMERIC_COLS.has(k) ? 0 : ''); });
    setForm(f);
    setFw(blankFw(r));
    if (fileRef.current) fileRef.current.value = '';
    setOpen(true);
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const patchFw = (p) => setFw((s) => ({ ...s, ...p, dirty: true }));

  const buildPayload = () => {
    const data = {};
    DEVICE_TYPE_COLUMNS.forEach((k) => {
      let v = form[k];
      if (NUMERIC_COLS.has(k)) v = v === '' || v == null ? 0 : Number(v);
      else if (FLAG_COLS.some(([fk]) => fk === k)) v = v ? 1 : 0;
      else v = v ?? '';
      data[k] = v;
    });
    // On add, drop DeviceID if the admin left it blank so the DB can assign it.
    if (!editing && (form.DeviceID === '' || form.DeviceID == null)) delete data.DeviceID;
    return data;
  };

  // The name the bin row will be keyed on once this saves — the picked file's
  // if there is one, otherwise whatever is already recorded.
  const fwName = fw ? (fw.file ? fw.file.name : fw.binFileName) : '';

  // Every OTHER device type that fetches the same filename, i.e. everything
  // this save also affects. Matched on BinFileName rather than on the id
  // because that is what the device actually downloads, and because a newly
  // picked file may key onto a row this type is not linked to yet.
  const fwAffected = useMemo(() => {
    if (!fwName) return [];
    return list.filter((t) => String(t.BinFileName || '').trim() === fwName
      && String(t.DeviceID) !== String(form && form.DeviceID));
  }, [list, fwName, form]);

  const fwCheck = fw && fw.file ? checkBinName(fw.file.name) : null;
  // Three independent things can be true, and they write different tables:
  //   fwRowDirty  a detail was edited or a file picked -> write OTABinFile
  //   fwRepoint   the dropdown moved                   -> write DeviceType
  //   fwPending   either of the above                  -> tell the operator
  const fwRowDirty = !!(fw && (fw.file || fw.dirty));
  const fwRepoint = !!(fw && Number(fw.otaBinFileId || 0) !== Number(fw.originalId || 0));
  const fwPending = fwRowDirty || fwRepoint;
  const fwImageWarning = fw && fw.file && fw.image && fw.image.ok
    ? chipNameMismatch(fw.file.name, fw.image.chip) : '';
  // ⚠️ bootloader.bin is a valid ESP image with the RIGHT chip id, and it sits
  // in the same build folder as the file you want. Only the app descriptor
  // separates them. OTA_CLAUDE.md §3 lists this as the mistake to expect.
  const fwNotApp = fw && fw.file ? notAnAppImage(fw.image) : '';
  // Blocked, not warned: a file with no 0xE9 is not firmware, one with no app
  // descriptor is a build artefact the device would reject, and a bin file name
  // that lands in the wrong folder 404s every device that asks.
  const fwBlocked = !!((fwCheck && !fwCheck.ok)
    || (fw && fw.image && fw.image.ok === false)
    || fwNotApp
    || (fw && fw.isNew && fw.file == null && !String(fw.binFileName || '').trim()));

  // Choosing a different bin file from the dropdown. Loads that row's details,
  // because UartPins / build date / URL / local path all belong to the ROW, not
  // to the device type — picking a different file must show that file's facts.
  const selectBin = async (value) => {
    if (value === '__new__') {
      setFw((s) => ({
        ...s,
        otaBinFileId: null,
        isNew: true,
        binFileName: '',
        hexFileUrl: '',
        localPath: '',
        uartPins: '',
        hexFileDateTime: '',
      }));
      return;
    }
    // ⚠️ NOT `dirty`. `dirty` means "the OTABinFile ROW needs writing"; simply
    // choosing an existing file does not change that row, it changes which row
    // this product points at. Conflating the two rewrote a shared bin row —
    // and its build date — every time somebody picked from the dropdown.
    const id = Number(value);
    const row = binList.find((b) => Number(b.OTABinFileId) === id);
    setFw((s) => ({
      ...s, otaBinFileId: id, isNew: false, binFileName: (row && row.BinFileName) || '',
    }));
    if (!row) return;
    try {
      const full = await getBinFileRow(row.BinFileName);
      if (!full) return;
      setFw((s) => (Number(s.otaBinFileId) !== id ? s : {
        ...s,
        hexFileUrl: full.HexFileUrl || '',
        localPath: full.HexFileLocalLocation || '',
        uartPins: full.UartPins || '',
        hexFileDateTime: full.HexFileDateTime || '',
      }));
    } catch (err) {
      // Non-fatal — the selection still stands, the detail fields just stay as
      // they were rather than being blanked on a failed lookup.
    }
  };

  // Everything that happens to a chosen file, shared by both ways of choosing
  // one — the system picker and the hidden <input> it falls back to. Split out
  // so the header check below cannot end up on only one of the two paths.
  const acceptBin = async (file) => {
    if (!file) { patchFw({ file: null, image: null }); return; }
    // The folder is the only part of the path a browser cannot supply, so it
    // is remembered and joined here. Still editable afterwards — a build from
    // somewhere else must not be recorded as if it came from the usual folder.
    patchFw({
      file,
      binFileName: file.name,
      localPath: joinLocalPath(fw.folder, file.name),
      image: null,
    });
    // Read the chip out of the first 16 bytes before anything is sent. This is
    // the only point at which a wrong-architecture image can still be caught —
    // afterwards the device stamps CodeUpdated before it even tries to flash.
    try {
      const image = await readEspImage(file);
      setFw((s) => (s.file === file ? { ...s, image } : s));
    } catch (err) {
      // ⚠️ NOT `ok: false` — that blocks the save. A header we could not read
      // (an old WebView without Blob.arrayBuffer, say) is not evidence of a bad
      // file, and refusing every upload because the CHECK broke would be a
      // worse failure than the one the check exists to catch.
      setFw((s) => (s.file === file ? { ...s, image: { unreadable: true } } : s));
    }
  };

  const pickBin = (e) => acceptBin((e.target.files && e.target.files[0]) || null);

  // ── The remembered build ─────────────────────────────────────────────────
  // Once a build has been picked, its FileSystemFileHandle is kept, so the same
  // file can be re-read on later visits with no picker at all — and re-read
  // means a rebuild at that path is picked up automatically. This removes the
  // step that was most likely to go wrong: choosing by hand from a folder of
  // near-identical names, where the wrong choice deploys the wrong firmware.
  //
  // ⚠️ IT IS ARMED, NOT FIRED. The button is one click and needs no picker, but
  // it is still a click. Uploading IS deploying — the bytes land at a fixed URL
  // and every device type on that name takes them on its next Update Code — so
  // an upload that ran merely because a screen was opened would re-flash a fleet
  // by navigation. See the header of core/devices/otaBinFiles.js.
  const [remembered, setRemembered] = useState(null);

  useEffect(() => {
    if (!open || !fw) { setRemembered(null); return undefined; }
    const name = fw.file ? fw.file.name : fw.binFileName;
    if (!name) { setRemembered(null); return undefined; }
    let alive = true;
    inspectRemembered(name)
      .then((r) => { if (alive) setRemembered(r); })
      .catch(() => { if (alive) setRemembered(null); });
    return () => { alive = false; };
  }, [open, fw && fw.binFileName, fw && fw.file]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Read the remembered file and publish it, in one action. Deliberately does
  // NOT touch the device type — this is "the file at that path has changed,
  // ship it", which is the whole job when a build has just been rebuilt.
  const uploadRemembered = async () => {
    setFwBusy(true); setMsg('');
    try {
      let file = remembered && remembered.file;
      // After a browser restart the handle is kept but the grant is not. This
      // is a click, so the gesture requirement is satisfied here and nowhere
      // else — which is why inspectRemembered never asks.
      if (!file && remembered && remembered.handle) {
        file = await regrantAndRead(remembered.handle);
        if (!file) { setMsg('Access to that file was not granted.'); return; }
      }
      if (!file) return;

      const check = checkBinName(file.name);
      if (!check.ok) { setMsg(check.error); return; }

      // The same header checks the manual path runs. A remembered file is not a
      // trusted file: the build at that path can be replaced by a bootloader or
      // a different chip's image between one upload and the next.
      const image = await readEspImage(file).catch(() => null);
      if (image && image.ok === false) { setMsg(image.error); return; }
      const notApp = notAnAppImage(image);
      if (notApp) { setMsg(notApp); return; }

      setFwStep('upload');
      const url = await uploadBinFile(file, { onProgress: setFwPct });
      setFwStep('record');
      await writeBinFileRow({
        binFileName: file.name,
        hexFileUrl: url,
        localPath: fw.localPath,
        uartPins: fw.uartPins,
        stampedAt: serverStamp(),
      });
      if (image && image.ok && image.sha8) rememberBuildSha(file.name, image.sha8);
      // Exactly one toast, naming the path that was published — the operator's
      // own words for what they wanted confirmed.
      setToast(`${fw.localPath || file.name} file Uploaded Successfully`);
      await load();
    } catch (err) {
      setMsg('The upload failed. The file was not published.');
    } finally {
      setFwBusy(false); setFwStep(''); setFwPct(null);
    }
  };

  // Prefer the system picker, which reopens the folder this bin file was last
  // taken from — see the note on pickBinFile for why it cannot simply be opened
  // at "Local location". Anything other than a cancel falls through to the
  // <input>, so a browser that has the API but refuses the call is not a dead
  // Upload button.
  const openBinPicker = async () => {
    if (supportsFolderMemory()) {
      try {
        const file = await pickBinFile(fw && fw.binFileName);
        if (file) await acceptBin(file);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;   // operator cancelled
      }
    }
    if (fileRef.current) fileRef.current.click();
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    setFwStep(''); setFwPct(null);
    try {
      const data = buildPayload();

      // A pure re-point — the dropdown changed, nothing was uploaded and no
      // detail was edited. Nothing to write to OTABinFile; just point the
      // product at the row that already exists.
      if (fwRepoint && !fw.file && !fwRowDirty) {
        data.OTABinFileId = Number(fw.otaBinFileId);
        data.OtaUpdateDateTime = serverStamp();
      }

      // Firmware FIRST: it yields the OTABinFileId that the DeviceType row has
      // to carry, so doing it here keeps the whole edit to a single UPDATE
      // instead of the C# tool's insert-then-update pair.
      if ((fw.file || fwRowDirty) && fwName) {
        // ⚠️ Only a real upload re-dates the build. HexFileDateTime is what
        // every device's flash time is compared against, so stamping it because
        // somebody fixed a typo in the path would report the entire fleet as
        // needing an update it has already had.
        const stampedAt = fw.file ? serverStamp() : null;
        let url = fw.hexFileUrl;

        if (fw.file) {
          setFwStep('upload');
          url = await uploadBinFile(fw.file, { onProgress: setFwPct });
        }
        setFwStep('record');
        let id = null;
        try {
          id = await writeBinFileRow({
            binFileName: fwName,
            hexFileUrl: url,
            localPath: fw.localPath,
            uartPins: fw.uartPins,
            stampedAt,
          });
        } catch (err) {
          // The bytes are already live at this point; say so rather than
          // reporting a flat "save failed" that reads as "nothing happened".
          const e = new Error(fw.file
            ? `${fw.file.name} was uploaded and IS live at ${url}, but recording it failed. The build date and links were not saved.`
            : 'Saving the firmware details failed.');
          e.handled = true;
          throw e;
        }
        setFwStep('');
        if (id != null) {
          data.OTABinFileId = id;
          // Stamps when this TYPE was pointed at the file — a different fact
          // from when the file was built, and only true if that changed.
          if (fw.file || id !== fw.otaBinFileId) data.OtaUpdateDateTime = serverStamp();
        }
        setLocalFolder(fw.folder);
        // Remember which build this name now holds, so a device can later be
        // checked against it rather than against a timestamp.
        if (fw.file && fw.image && fw.image.sha8) rememberBuildSha(fwName, fw.image.sha8);
      }

      if (editing) {
        await updateDeviceType(data, `DeviceID = ${form.DeviceID}`);
      } else {
        await addDeviceType(data);
      }
      await load();
      setOpen(false);
    } catch (e) {
      setMsg(e && e.handled ? e.message : 'Save failed — check the connection and try again.');
    } finally {
      setSaving(false);
      setFwStep(''); setFwPct(null);
    }
  };

  const valid = form && String(form.DeviceName || '').trim().length > 0 && !fwBlocked;

  // ── Fields are sized to their CONTENT, not to a 12-column grid ───────────
  // A one-digit channel count occupying a quarter of the dialog is what made
  // this form four screens tall. Every field below carries the width its value
  // actually needs; the row wraps, so nothing is cut off on a narrow window.
  //
  // Helper text is a tooltip, not a third line: each `helperText` adds ~20px
  // under EVERY field it is on, which across thirty fields is a screenful of
  // its own.
  const numField = (k, label, w = W.num) => (
    <TextField
      key={k} label={label} type="number" size="small" sx={{ width: w }}
      value={form[k] ?? ''}
      onChange={(e) => patch({ [k]: e.target.value === '' ? '' : num(e.target.value) })}
    />
  );
  const textField = (k, label, opts = {}) => (
    <TextField
      key={k} label={label} size="small"
      sx={opts.grow ? { flex: `1 1 ${opts.w || W.link}px`, minWidth: 180 } : { width: opts.w || W.med }}
      value={form[k] ?? ''}
      onChange={(e) => patch({ [k]: e.target.value })}
      {...(opts.tf || {})}
    />
  );

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>Device Types</Typography>
          <Tooltip title="Add device type">
            <IconButton color="inherit" onClick={openNew}><AddIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search name / type / ID…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${filtered.length} of ${list.length} device types`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add device type</Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Card} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['ID', 'Device Name', 'Type', 'Alexa', 'Out', 'On/Off', 'Dim', 'In', 'MRP', 'Firmware', ''].map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align={i >= 4 && i <= 8 ? 'center' : 'left'}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.DeviceID} hover sx={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
                    <TableCell><Chip size="small" icon={<MemoryIcon />} label={r.DeviceID} /></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.DeviceName || '—'}</TableCell>
                    <TableCell>{r.DeviceType || '—'}</TableCell>
                    <TableCell>{r.AlexaType || '—'}</TableCell>
                    <TableCell align="center">{r.Outputs}</TableCell>
                    <TableCell align="center">{r.On_off}</TableCell>
                    <TableCell align="center">{r.Dimming}</TableCell>
                    <TableCell align="center">{r.Inputs}</TableCell>
                    {/* Monospace and untruncated: the difference between
                        OtaForSwitching and OtaForSwitchingNewDesign is the
                        whole answer, and it is at the end of the string. */}
                    <TableCell>
                      {r.BinFileName ? (
                        <Tooltip title={r.HexFileUrl || r.BinFileName}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {r.BinFileName}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No device types found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add / Edit dialog */}
      {/* ⚠️ WIDE AND SHORT ON PURPOSE. `md` gave a 900px dialog whose fields
          were laid out on a 12-column grid, so a single-digit channel count was
          200px wide and the form ran four screens deep — the operator scrolled
          past everything to reach anything. `lg` plus content-width fields and
          the paired sections below fit it on one screen on a normal display.
          The 8px margin (against MUI's default 32) is worth another two rows on
          a laptop, which is where this is actually used. */}
      <Dialog
        open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { m: 1, maxHeight: 'calc(100% - 16px)' } }}
      >
        <DialogTitle sx={{ py: 1.25, fontSize: 16, fontWeight: 700 }}>
          {editing ? `Edit Device Type — ${form?.DeviceName || ''} (ID ${form?.DeviceID})` : 'Add Device Type'}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 1.5 }}>
          {form && (
            <Stack spacing={1.75}>
              {msg && <Alert severity="error">{msg}</Alert>}

              <Section title="Identity">
                {textField('DeviceName', 'Device Name', { w: W.name })}
                <TextField
                  label="Device ID" type="number" size="small" sx={{ width: W.count }}
                  value={form.DeviceID ?? ''} disabled={editing}
                  title={editing ? 'Primary key — cannot be changed' : 'Leave blank to let the database assign it'}
                  onChange={(e) => patch({ DeviceID: e.target.value === '' ? '' : num(e.target.value) })}
                />
                {numField('SortOrder', 'Sort', W.count)}
                <TextField select label="Device Type" size="small" sx={{ width: W.pick }}
                  value={form.DeviceType || ''} onChange={(e) => patch({ DeviceType: e.target.value })}>
                  {DEVICE_TYPE_OPTS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <TextField select label="Alexa Type" size="small" sx={{ width: W.pick }}
                  value={form.AlexaType || ''} onChange={(e) => patch({ AlexaType: e.target.value })}>
                  {ALEXA_TYPE_OPTS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                {textField('DeviceInfoId', 'Info Id', { w: W.code })}
                {textField('Status', 'Status', { w: W.code })}
                {textField('Discription', 'Description', { grow: true })}
              </Section>

              {/* Three sections of short numbers, side by side. Stacked they
                  were three near-empty rows; paired they are one. */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Section title="Channels">
                  {numField('Outputs', 'Outputs', W.count)}
                  {numField('On_off', 'On/Off', W.count)}
                  {numField('Dimming', 'Dimming', W.count)}
                  {numField('Inputs', 'Inputs', W.count)}
                  {textField('DefaultValue', 'Default Value', { w: W.med })}
                </Section>

                {/* Pricing and production merged: nine short numbers under one
                    caption flow better than two captions over two half-empty
                    rows, and the saving is a whole heading. */}
                <Section title="Pricing & production" sx={{ flex: '1 1 auto' }}>
                  {numField('Mrp', 'MRP', W.num)}
                  {numField('PriceWithWifi', 'With Wi-Fi', W.num)}
                  {numField('PriceWithoutWifi', 'Without Wi-Fi', W.num)}
                  {numField('Discount', 'Discount', W.num)}
                  {numField('Warranty', 'Warranty (mo)', W.num)}
                  {numField('BlankPcb', 'Blank PCB', W.count)}
                  {numField('Assembled', 'Assembled', W.count)}
                  {numField('Tested', 'Tested', W.count)}
                  {numField('Packed', 'Packed', W.count)}
                </Section>
              </Box>

              {/* Checkboxes wrap on their own width instead of sitting in a
                  4-column grid — eleven of them fit two rows, not three. */}
              <Section title="Capabilities">
                {FLAG_COLS.map(([k, label]) => (
                  <FormControlLabel
                    key={k}
                    sx={{ mr: 1.5, ml: -0.5 }}
                    control={<Checkbox size="small" sx={{ py: 0.25 }} checked={!!Number(form[k])}
                      onChange={(e) => patch({ [k]: e.target.checked ? 1 : 0 })} />}
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                ))}
              </Section>

              {/* ── Firmware (OTABinFile) ────────────────────────────────
                  Reads through the join, writes to OTABinFile. See the file
                  header for why this one section does not go through
                  DEVICE_TYPE_COLUMNS. */}
              {fw && (
                <Section
                  title="Firmware (OTA bin file)"
                  right={(
                    <>
                      {fw.otaBinFileId != null && (
                        <Chip size="small" variant="outlined" label={`id ${fw.otaBinFileId}`} sx={{ height: 18, fontSize: 10.5 }} />
                      )}
                      <Box sx={{ flex: 1 }} />

                      {/* ── The remembered build, one click, no picker ────────
                          Appears only once this bin file has been picked once
                          on this computer. From then on the same file is
                          re-read — so a rebuild at that path publishes without
                          anyone choosing anything, which is the step that was
                          most likely to go wrong. */}
                      {remembered && remembered.state === HANDLE_GRANTED && (
                        <Button
                          size="small" variant="contained" color="success"
                          startIcon={<UploadFileIcon />}
                          onClick={uploadRemembered}
                          disabled={saving || fwBusy}
                          sx={{ textTransform: 'none', py: 0.1 }}
                          title={fw.localPath || remembered.name}
                        >
                          {`Upload ${remembered.name} now`}
                        </Button>
                      )}
                      {/* The handle survived; the grant did not. One click
                          restores it — which is why the check never asks. */}
                      {remembered && remembered.state === HANDLE_PROMPT && (
                        <Button
                          size="small" variant="outlined" color="success"
                          startIcon={<UploadFileIcon />}
                          onClick={uploadRemembered}
                          disabled={saving || fwBusy}
                          sx={{ textTransform: 'none', py: 0.1 }}
                          title="Chrome forgets file access when it restarts. One click restores it and publishes."
                        >
                          {`Allow & upload ${remembered.name}`}
                        </Button>
                      )}
                      {remembered && remembered.state === HANDLE_MISSING && (
                        <Chip
                          size="small" color="warning" variant="outlined"
                          label="remembered file has moved"
                          sx={{ height: 20, fontSize: 10.5 }}
                        />
                      )}

                      <Button
                        size="small" variant="outlined" startIcon={<UploadFileIcon />}
                        onClick={openBinPicker}
                        disabled={saving || fwBusy}
                        sx={{ textTransform: 'none', py: 0.1 }}
                        title={supportsFolderMemory()
                          ? 'Reopens the folder this bin file was last taken from.'
                          : 'A plain-HTTP page cannot reopen a folder — copy the local path and paste it into the dialog.'}
                      >
                        {fw.file ? 'Choose a different .bin' : 'Upload a .bin'}
                      </Button>
                      {/* ⚠️ No `accept` on native: Android turns it into MIME
                          types, .bin has none, and several pickers then show an
                          empty folder. checkBinName is the real guard. */}
                      <input
                        ref={fileRef} type="file" hidden onChange={pickBin}
                        {...(isNativePlatform() ? {} : { accept: '.bin' })}
                      />
                    </>
                  )}
                >
                  {/* Both alerts are single-line and full-width, so they push
                      the fields down only while they have something to say. */}
                  {fw.file && (
                    <Alert
                      severity={fwBlocked ? 'error' : (fwImageWarning ? 'warning' : 'info')}
                      sx={{ width: '100%', py: 0, '& .MuiAlert-message': { py: 0.75 } }}
                      icon={<UploadFileIcon fontSize="small" />}
                    >
                      <Typography variant="body2">
                        {(fwCheck && !fwCheck.ok && fwCheck.error)
                          || (fw.image && fw.image.ok === false && fw.image.error)
                          || fwNotApp
                          || `${fw.file.name} · ${(fw.file.size / 1024).toFixed(1)} KB`
                             + `${fw.image && fw.image.chip
                               ? ` · ${fw.image.chip === ESP8266_CLASS
                                 ? 'ESP8266-class image (header carries no chip id)'
                                 : `built for ${fw.image.chip}`}`
                               : ''}`
                             // Which BUILD, not just which chip — the descriptor
                             // carries what `esptool image_info` prints.
                             + `${fw.image && fw.image.isApp
                               ? ` · ${fw.image.projectName} v${fw.image.appVersion} (IDF ${fw.image.idfVersion})`
                               : ''}`
                             + ` — uploads to ${EXPECTED_BIN_DIR} on Save.`}
                      </Typography>
                      {/* The mismatch that already happened once: a file named
                          for an ESP8684 (C2) module holding an ESP32-C3 image. */}
                      {fwImageWarning && (
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                          {fwImageWarning}
                        </Typography>
                      )}
                      {fwCheck && fwCheck.ok && fwCheck.warning && (
                        <Typography variant="caption" sx={{ display: 'block' }}>{fwCheck.warning}</Typography>
                      )}
                    </Alert>
                  )}

                  {/* The one thing that must be seen before saving: this row is
                      shared, so the blast radius is not this device type. */}
                  {/* A re-point affects only this product; editing or replacing
                      the file affects every product on it. Saying "30 types
                      affected" for a re-point would be false, and false warnings
                      are how real ones get ignored. */}
                  {fwRowDirty && fwAffected.length > 0 && (
                    <Alert severity="warning" sx={{ width: '100%', py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
                      <Typography variant="body2">
                        {`${fwAffected.length} other device type${fwAffected.length === 1 ? '' : 's'} use ${fwName}`}
                        {fw.file
                          ? ' and will fetch these new bytes on their next Update Code. The old file is overwritten and is not kept.'
                          : ' and share these details.'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {fwAffected.slice(0, 6).map((t) => `${t.DeviceID} ${t.DeviceName}`).join(' · ')}
                        {fwAffected.length > 6 ? ` · +${fwAffected.length - 6} more` : ''}
                      </Typography>
                    </Alert>
                  )}

                  {/* ⚠️ The type-level choice cannot express a mixed fleet. If
                      some 4 Touch are ESP8266 and some ESP32-C3, this dropdown
                      can only be right for one of them — the other has to be
                      set per device on DevicesInstalled.OTABinFileId, which
                      overrides this. Verified live: 935809 is a DeviceID 49
                      running OtaForTuya while type 49 points elsewhere. */}
                  {fwRepoint && !fw.isNew && (
                    <Alert severity="info" sx={{ width: '100%', py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
                      <Typography variant="body2">
                        {`Every ${form.DeviceName || 'device'} without its own firmware setting will move to ${fwName}.`}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Devices carrying their own OTABinFileId keep it — that per-device
                        value overrides this one, and it is how a product on two different
                        chips is handled.
                      </Typography>
                    </Alert>
                  )}

                  {/* The C# tool's CbOTABinFile. Picking a different file here
                      re-points this product; the four detail fields below
                      belong to the picked ROW and reload with it. */}
                  <TextField
                    select label="OTA bin file" size="small" sx={{ width: W.bin }}
                    value={fw.isNew ? '__new__' : (fw.otaBinFileId ?? '')}
                    onChange={(e) => selectBin(e.target.value)}
                    disabled={saving}
                  >
                    {binList.map((b) => (
                      <MenuItem key={b.OTABinFileId} value={b.OTABinFileId}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                          {b.BinFileName}
                        </Typography>
                      </MenuItem>
                    ))}
                    <MenuItem value="__new__">
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>New bin file…</Typography>
                    </MenuItem>
                  </TextField>

                  {/* Only when the file is genuinely new is the name typed —
                      otherwise it is the dropdown's, and editing it would
                      silently key the upsert onto a different row. */}
                  {fw.isNew && (
                    <TextField
                      label="New bin file name" size="small" sx={{ width: W.name }}
                      value={fw.binFileName}
                      onChange={(e) => patchFw({ binFileName: e.target.value })}
                      disabled={!!fw.file || saving}
                      placeholder="ESP8685WROOM04.bin"
                      title="Taken from the file you pick, or typed to create the row first."
                    />
                  )}
                  <TextField
                    label="UART pins" size="small" sx={{ width: W.code }}
                    value={fw.uartPins}
                    onChange={(e) => patchFw({ uartPins: e.target.value })}
                    title="e.g. D7D8. Blank is normal."
                  />
                  <TextField
                    label="Build date" size="small" sx={{ width: W.med }}
                    value={fw.hexFileDateTime ? String(fw.hexFileDateTime).replace('T', ' ').slice(0, 19) : ''}
                    disabled
                    title="Set by the server when a file is uploaded."
                  />
                  <TextField
                    label="Server location" size="small"
                    sx={{ flex: `1 1 ${W.link}px`, minWidth: 240 }}
                    value={fw.hexFileUrl}
                    onChange={(e) => patchFw({ hexFileUrl: e.target.value })}
                    title="OTABinFile.HexFileUrl — filled in by the upload. This is what the device downloads."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>,
                    }}
                  />

                  {/* HexFileLocalLocation. A browser cannot read a file's path,
                      so the folder is remembered per computer and joined to the
                      picked filename. */}
                  <TextField
                    label="Local bin folder (this computer)" size="small"
                    sx={{ flex: `1 1 ${W.link}px`, minWidth: 220 }}
                    value={fw.folder}
                    onChange={(e) => setFw((s) => ({
                      ...s,
                      folder: e.target.value,
                      localPath: s.file ? joinLocalPath(e.target.value, s.file.name) : s.localPath,
                    }))}
                    placeholder="H:/Mix_Data/espressif/claude/OtaForSwitching"
                    title="Remembered on this computer and joined to the file name."
                  />
                  {/* The copy button is not a nicety — it is the half of
                      "open this folder" that a page IS allowed to do. Windows'
                      and macOS' Open dialogs both jump straight to a full path
                      pasted into their file-name box, so this works on the
                      first pick, and on the deployed plain-HTTP site where the
                      picker's folder memory does not exist at all. */}
                  <TextField
                    label="Local location" size="small"
                    sx={{ flex: `1 1 ${W.link}px`, minWidth: 220 }}
                    value={fw.localPath}
                    onChange={(e) => patchFw({ localPath: e.target.value })}
                    title="OTABinFile.HexFileLocalLocation — where this build came from."
                    InputProps={{
                      endAdornment: fw.localPath ? (
                        <InputAdornment position="end">
                          <Tooltip title={copied ? 'Copied — paste it into the file dialog'
                            : 'Copy the path, then paste it into the file dialog’s name box'}
                          >
                            <IconButton
                              size="small" edge="end"
                              onClick={() => {
                                // No clipboard on an insecure context either, so
                                // a failure selects the text instead of doing
                                // nothing — the operator can still copy it.
                                const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                  navigator.clipboard.writeText(fw.localPath).then(done).catch(() => {});
                                }
                              }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  {saving && fwStep && (
                    <Box sx={{ width: '100%' }}>
                      <LinearProgress
                        variant={fwStep === 'upload' && fwPct != null ? 'determinate' : 'indeterminate'}
                        value={fwPct ?? 0}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {fwStep === 'upload'
                          ? `Uploading ${fw.file ? fw.file.name : ''}${fwPct != null ? ` — ${fwPct}%` : ''}`
                          : 'Recording the build…'}
                      </Typography>
                    </Box>
                  )}
                </Section>
              )}

              <Section title="Links & image">
                {/* ⚠️ These are the LEGACY link columns, not the firmware
                    above. `OTAUpdateLink` is not what GetDeviceOTAUrlLink reads
                    and mostly holds a lower-cased URL that 404s — see
                    core/devices/firmwareInfo.js. Kept editable because the C#
                    tool writes them, but the section above is the live one. */}
                {textField('OTAUpdateLink', 'OTA Link (legacy)', { grow: true })}
                {textField('OTAUpdateLinkNuvo', 'OTA Link (Nuvo)', { grow: true })}
                {textField('LocalCodeLink', 'Local Code Link', { grow: true })}
                {textField('LocalCodeLinkNuvo', 'Local Code Link (Nuvo)', { grow: true })}
                {textField('ManualPdfLink', 'Manual PDF Link', { grow: true })}
                {textField('AppUrlLink', 'App URL Link', { grow: true })}
                {textField('ImageLocation', 'Image file', { grow: true })}
                <TextField select label="Image Type" size="small" sx={{ width: W.pick }}
                  value={form.ImageType || ''} onChange={(e) => patch({ ImageType: e.target.value })}>
                  {['Product', 'Compulsory'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              </Section>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 0.75 }}>
          {/* Says which tables the button is about to write, because one of
              them is not this device type's alone. */}
          {fwPending && !saving && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
              {fw && fw.file ? `Saves the type and uploads ${fw.file.name}`
                : (fwRowDirty ? 'Saves the type and the shared bin-file details'
                  : `Saves the type and points it at ${fwName}`)}
              {fwRowDirty && fwAffected.length > 0
                ? ` — ${fwAffected.length} other type${fwAffected.length === 1 ? '' : 's'} affected`
                : ''}
            </Typography>
          )}
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color={fwRowDirty && fwAffected.length > 0 ? 'warning' : 'primary'}
            onClick={save}
            disabled={!valid || saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ⚠️ ONE toast, three seconds, naming the path that was published.
          Deliberately the only success signal for this action: the operator
          asked for one, and a second confirmation (an Alert that stays, a
          re-render of the row) turns a clean "done" into something to dismiss. */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast('')} sx={{ maxWidth: 640 }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
